import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import BeforeAfterSlider from './BeforeAfterSlider';

const HERO_IMAGE =
  'https://public.readdy.ai/ai/img_res/1703db1369573a452f8aa61e7c66bda2.jpg';

export default function HeroNew() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <main>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image – real <img> for LCP optimisation */}
        <img
          src={HERO_IMAGE}
          alt="Schwarzwald Hintergrund – BR Webdesign Titisee-Neustadt"
          className="absolute inset-0 w-full h-full object-cover object-center"
          fetchPriority="high"
          loading="eager"
          decoding="async"
          aria-hidden="true"
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/65" />

        {/* Content */}
        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 text-center pt-24 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <span className="inline-block text-brand-beige-light text-xs sm:text-sm font-semibold tracking-widest uppercase mb-6 px-4 py-2 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm">
              Webdesign · AI Chatbots · SEO – Titisee-Neustadt
            </span>
          </motion.div>

          <motion.h1
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6"
            style={{ fontFamily: 'Caprasimo, cursive' }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: 'easeOut' }}
          >
            Ihre Webseite.<br />
            <span className="text-brand-beige-light">Ihr Wachstum.</span>
          </motion.h1>

          <motion.p
            className="text-lg sm:text-xl md:text-2xl text-white/85 max-w-3xl mx-auto mb-10 leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
          >
            Ihre Webseite wird zur digitalen Verkaufsmaschine – für Ihr Wachstum.
            Modernes Design, AI-Chatbots und SEO-Optimierung aus Titisee-Neustadt.
            <strong className="text-white"> Fertig in unter 1 Woche.</strong>
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.45, ease: 'easeOut' }}
          >
            <Link
              to="/contact"
              className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-light text-white px-8 py-4 rounded-lg text-base font-semibold transition-all cursor-pointer whitespace-nowrap shadow-xl hover:shadow-2xl hover:scale-105 flex items-center justify-center gap-2"
            >
              <i className="ri-rocket-line" />
              Kostenloses Erstgespräch
            </Link>
            <Link
              to="/pricing"
              className="w-full sm:w-auto bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-lg text-base font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-price-tag-3-line" />
              Preise ansehen
            </Link>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            className="flex flex-wrap items-center justify-center gap-6 mt-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.65 }}
          >
            {[
              { icon: 'ri-timer-flash-line', text: 'Fertig in &lt; 1 Woche' },
              { icon: 'ri-shield-check-line', text: 'DSGVO-konform' },
              { icon: 'ri-customer-service-2-line', text: 'Persönlicher Support' },
              { icon: 'ri-money-euro-circle-line', text: 'Ab 899 €' },
            ].map((badge, i) => (
              <div
                key={i}
                className="flex items-center gap-2 text-white/80 text-sm bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/15"
              >
                <i className={`${badge.icon} text-brand-beige-light`} />
                <span dangerouslySetInnerHTML={{ __html: badge.text }} />
              </div>
            ))}
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50 will-change-transform"
          initial={{ opacity: 0 }}
          animate={{ opacity: scrolled ? 0 : 1 }}
          transition={{ duration: 0.4 }}
        >
          <span className="text-xs tracking-widest uppercase">Scrollen</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
          >
            <i className="ri-arrow-down-line text-xl" />
          </motion.div>
        </motion.div>
      </section>

      <BeforeAfterSlider />
    </main>
  );
}
